import Symbol from './Symbol';
import SymbolMap from './SymbolMap';
import NumberPad from './NumberPad';
import ComboBadge from './ComboBadge';
import { type Feedback } from '@/pages/dsst-spick/dsstConfig';

interface GameScreenProps {
  symbolMap: number[];
  currentSymbol: number;
  selectedNumber: number | null;
  feedback: Feedback;
  remainingSeconds: number;
  combo: number;
  setChangeNotice: boolean;
  setIndex: number;
  numberOrder: number[];
  onSelect: (n: number) => void;
}

// 스픽(Spick) 버전 게임 화면
// - 공통 요소: 연한 블루 배경, "기호 맞추기" 헤더 밴드 + 남은 시간 배지, 안내 문구, 흰 게임보드
// - 게임보드는 남은 높이를 그대로 사용하고, 하단 숫자 패드는 Safe Area 위에 고정한다.
export default function GameScreen({
  symbolMap,
  currentSymbol,
  selectedNumber,
  feedback,
  remainingSeconds,
  combo,
  setChangeNotice,
  setIndex,
  numberOrder,
  onSelect,
}: GameScreenProps) {
  const isLastTen = remainingSeconds <= 10;
  const isLastFive = remainingSeconds <= 5;
  const timeText = `00:${String(remainingSeconds).padStart(2, '0')}`;

  const guidanceText = setChangeNotice
    ? '기호 그룹이 교체됐어요!'
    : '이 기호와 짝이 되는 숫자를 눌러주세요';

  return (
    <div className="relative flex flex-col h-full sh-game-bg pb-[var(--sh-screen-pb)] animate-fade-in-up">
      {/* 게임명 + 남은 시간 (스픽 헤더 밴드) */}
      <div className="shrink-0 h-[44px] sh-header-bg px-5 flex items-center justify-between">
        <h1 className="text-[14px] font-bold text-foreground-950">기호 맞추기</h1>
        <span
          className={`inline-flex items-center h-7 px-3 rounded-full text-white text-[13px] font-bold tabular-nums whitespace-nowrap ${
            isLastTen ? 'bg-accent-500' : ''
          } ${isLastFive ? 'animate-timer-pulse' : ''}`}
          style={isLastTen ? undefined : { backgroundColor: 'var(--sh-stage-badge-bg)' }}
        >
          {timeText}
        </span>
      </div>

      {/* 현재 행동 안내 */}
      <div className="h-[var(--sh-guidance-height)] shrink-0 flex items-center justify-center px-5">
        <p className="text-[16px] font-bold leading-snug text-center text-foreground-950">
          <span className="mr-1.5" aria-hidden="true">
            {setChangeNotice ? '🔄' : '💡'}
          </span>
          {guidanceText}
        </p>
      </div>

      {/* 게임보드: 기호-숫자 대응표 + 현재 기호 */}
      <div className="flex-1 min-h-0 flex flex-col px-5">
        <div className="relative w-full flex-1 min-h-0 rounded-3xl overflow-hidden bg-white sh-dot-grid flex flex-col px-4 pt-3 pb-2">
          {/* 대응표 */}
          <div className="shrink-0 rounded-2xl bg-primary-50 border border-primary-100 px-2 py-2.5">
            <SymbolMap
              symbolMap={symbolMap}
              setIndex={setIndex}
              symbolSizeClass="w-[var(--dsst-map-symbol)] h-[var(--dsst-map-symbol)]"
            />
          </div>

          {/* 연속 정답 배지 */}
          <ComboBadge combo={combo} />

          {/* 현재 문제 기호 */}
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center">
            <p className="text-[14px] font-semibold text-foreground-700 text-center leading-snug">
              이 기호와 짝이 되는 숫자는?
            </p>
            <div
              key={`${setIndex}-${currentSymbol}`}
              className={`mt-3 flex items-center justify-center text-accent-500 ${
                feedback === 'correct' ? 'animate-symbol-correct' : 'animate-symbol-in'
              }`}
            >
              <Symbol
                symbolId={currentSymbol}
                setIndex={setIndex}
                className="w-[var(--dsst-symbol-size)] h-[var(--dsst-symbol-size)]"
              />
            </div>

            {/* 정답/오답 즉각 피드백 */}
            <div className="mt-3 flex items-center justify-center h-9">
              {feedback === 'correct' && (
                <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-base font-bold whitespace-nowrap animate-combo-bump">
                  정답
                </span>
              )}
              {feedback === 'wrong' && (
                <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-foreground-100 text-foreground-700 text-base font-bold whitespace-nowrap animate-combo-bump">
                  오답
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 숫자 선택: 게임보드 아래 고정 */}
      <div className="shrink-0 px-5 pt-3">
        <NumberPad
          onSelect={onSelect}
          selectedNumber={selectedNumber}
          feedback={feedback}
          disabled={feedback !== null}
          order={numberOrder}
        />
      </div>
    </div>
  );
}